import type { Theme } from '@/contexts/ThemeContext/ThemeContext';
import type { SectionColor } from '@/lib/sectionColor';

import hubStaticBlueDark from '@/assets/lottie/hub-icon/HubIcon_Static_Blue_Dark.svg';
import hubStaticBlueLight from '@/assets/lottie/hub-icon/HubIcon_Static_Blue_Light.svg';
import hubStaticGreenDark from '@/assets/lottie/hub-icon/HubIcon_Static_Green_Dark.svg';
import hubStaticGreenLight from '@/assets/lottie/hub-icon/HubIcon_Static_Green_Light.svg';
import hubStaticMarineDark from '@/assets/lottie/hub-icon/HubIcon_Static_Marine_Dark.svg';
import hubStaticMarineLight from '@/assets/lottie/hub-icon/HubIcon_Static_Marine_Light.svg';
import hubStaticPurpleDark from '@/assets/lottie/hub-icon/HubIcon_Static_Purple_Dark.svg';
import hubStaticPurpleLight from '@/assets/lottie/hub-icon/HubIcon_Static_Purple_Light.svg';

export const HUB_COLORS = ['Blue', 'Green', 'Marine', 'Purple'] as const;
export type HubColor = typeof HUB_COLORS[number];

// The artwork predates the shared palette names and uses its own. Exhaustive, so a new section
// color will not compile until it has an asset.
const HUB_COLOR_BY_SECTION: Record<SectionColor, HubColor> = {
    marine: 'Marine',
    amethyst: 'Purple',
    bluebell: 'Blue',
    sage: 'Green',
};

export const hubColorForSection = (color: SectionColor): HubColor => HUB_COLOR_BY_SECTION[color];

// Statics spell the theme `Light`/`Dark`, transitions `Lightmode`/`Darkmode`. Contained here.
const STATIC_BY_COLOR: Record<HubColor, Record<Theme, string>> = {
    Blue: { light: hubStaticBlueLight, dark: hubStaticBlueDark },
    Green: { light: hubStaticGreenLight, dark: hubStaticGreenDark },
    Marine: { light: hubStaticMarineLight, dark: hubStaticMarineDark },
    Purple: { light: hubStaticPurpleLight, dark: hubStaticPurpleDark },
};

// Explicit dynamic imports rather than `import.meta.glob`, which breaks Cypress's non-module
// context. Each pair is its own chunk so only the one played is fetched.
const TRANSITION_LOADERS: Record<string, () => Promise<{ default: unknown }>> = {
    Blue_Green_Lightmode: () => import('@/assets/lottie/hub-icon/Blue_Green_Lightmode.json'),
    Blue_Marine_Lightmode: () => import('@/assets/lottie/hub-icon/Blue_Marine_Lightmode.json'),
    Blue_Purple_Lightmode: () => import('@/assets/lottie/hub-icon/Blue_Purple_Lightmode.json'),
    Green_Blue_Lightmode: () => import('@/assets/lottie/hub-icon/Green_Blue_Lightmode.json'),
    Green_Marine_Lightmode: () => import('@/assets/lottie/hub-icon/Green_Marine_Lightmode.json'),
    Green_Purple_Lightmode: () => import('@/assets/lottie/hub-icon/Green_Purple_Lightmode.json'),
    Marine_Blue_Lightmode: () => import('@/assets/lottie/hub-icon/Marine_Blue_Lightmode.json'),
    Marine_Green_Lightmode: () => import('@/assets/lottie/hub-icon/Marine_Green_Lightmode.json'),
    Marine_Purple_Lightmode: () => import('@/assets/lottie/hub-icon/Marine_Purple_Lightmode.json'),
    Purple_Blue_Lightmode: () => import('@/assets/lottie/hub-icon/Purple_Blue_Lightmode.json'),
    Purple_Green_Lightmode: () => import('@/assets/lottie/hub-icon/Purple_Green_Lightmode.json'),
    Purple_Marine_Lightmode: () => import('@/assets/lottie/hub-icon/Purple_Marine_Lightmode.json'),
    Blue_Green_Darkmode: () => import('@/assets/lottie/hub-icon/Blue_Green_Darkmode.json'),
    Blue_Marine_Darkmode: () => import('@/assets/lottie/hub-icon/Blue_Marine_Darkmode.json'),
    Blue_Purple_Darkmode: () => import('@/assets/lottie/hub-icon/Blue_Purple_Darkmode.json'),
    Green_Blue_Darkmode: () => import('@/assets/lottie/hub-icon/Green_Blue_Darkmode.json'),
    Green_Marine_Darkmode: () => import('@/assets/lottie/hub-icon/Green_Marine_Darkmode.json'),
    Green_Purple_Darkmode: () => import('@/assets/lottie/hub-icon/Green_Purple_Darkmode.json'),
    Marine_Blue_Darkmode: () => import('@/assets/lottie/hub-icon/Marine_Blue_Darkmode.json'),
    Marine_Green_Darkmode: () => import('@/assets/lottie/hub-icon/Marine_Green_Darkmode.json'),
    Marine_Purple_Darkmode: () => import('@/assets/lottie/hub-icon/Marine_Purple_Darkmode.json'),
    Purple_Blue_Darkmode: () => import('@/assets/lottie/hub-icon/Purple_Blue_Darkmode.json'),
    Purple_Green_Darkmode: () => import('@/assets/lottie/hub-icon/Purple_Green_Darkmode.json'),
    Purple_Marine_Darkmode: () => import('@/assets/lottie/hub-icon/Purple_Marine_Darkmode.json'),
};

export const getHubStaticSrc = (color: HubColor, theme: Theme): string =>
    STATIC_BY_COLOR[color][theme];

const transitionKeyFor = (from: HubColor, to: HubColor, theme: Theme): string => {
    const mode = theme === 'dark' ? 'Darkmode' : 'Lightmode';
    return `${from}_${to}_${mode}`;
};

// Null when the pair has no asset, since same-color transitions are never authored. Callers
// handle that by not animating.
export const loadHubTransition = async (
    from: HubColor,
    to: HubColor,
    theme: Theme,
): Promise<unknown | null> => {
    const loader = TRANSITION_LOADERS[transitionKeyFor(from, to, theme)];
    if (!loader) {
        return null;
    }
    try {
        const module = await loader();
        return module.default ?? null;
    }
    catch {
        return null;
    }
};

/** Longest a transition may run before we give up waiting for its `complete` event. */
export const HUB_TRANSITION_TIMEOUT_MS = 2400;
